// iter485 — Explicit GDPR consent modal for Health Intelligence module.
// Requirements:
//   - Triggered when GET /health-intelligence/profile returns consent=null
//     or consent.granted=false.
//   - Lists exactly which medical data categories will be processed and which
//     sub-processors receive them.
//   - Lets the user toggle EACH purpose (personalization, ai_analysis,
//     research_aggregated) independently — Art. 7(3) GDPR "granular consent".
//   - Submits POST /health-intelligence/consent with selected purposes.
//   - Modal cannot be dismissed without an explicit decision (accept OR back).

import React, { useState } from "react";
import axios from "axios";
import { AlertTriangle, Shield, ShieldCheck, FileText, Activity, Sparkles, X, Loader2 } from "lucide-react";

const T = {
  bg: {
    title:        "Здравна интелигентност — изрично GDPR съгласие",
    intro:        "Health Intelligence обработва Ваши специални категории лични данни (чл. 9 GDPR: здравни данни). Преди да я ползваме, имаме нужда от Вашето изрично, информирано и доброволно съгласие.",
    sub_title:    "Какво ще обработваме (изберете кои цели одобрявате):",
    purposes: [
      {
        id: "personalization",
        title: "Персонализация",
        desc: "Запазваме Вашите състояния, симптоми и семейна история локално в нашата база, за да Ви показваме персонализирано съдържание. Без AI обработка.",
        required: true,
      },
      {
        id: "ai_analysis",
        title: "AI анализ (OpenAI GPT-4o)",
        desc: "Изпращаме АНОНИМИЗИРАНИ конклюзии (състояния, биомаркери, фенотип) към OpenAI Inc. (САЩ) за генериране на персонализирани AI препоръки. Лични идентификатори (ЕГН, име, имейл) се маскират ПРЕДИ изпращане. Под SCC + zero-retention режим.",
        required: false,
      },
      {
        id: "research_aggregated",
        title: "Анонимизирани изследвания",
        desc: "Използваме ПЪЛНО анонимизирани (без user_id, без timestamp) агрегати на популацията за подобряване на алгоритмите (напр. средни DIL стойности по фенотип). Не можем да върнем към Вас.",
        required: false,
      },
    ],
    data_categories_title: "Данни, които ще обработваме:",
    data_categories: [
      "Хронични състояния (PCOS, Хашимото, диабет тип 2 и др.)",
      "Симптоми (умора, болки, проблеми със съня — със скала 0-10)",
      "Семейна история (роднинско отношение + състояние, БЕЗ имена)",
      "Лабораторни биомаркери (TSH, глюкоза, инсулин, липиден профил и др.)",
      "Метаболитни индекси, изчислени от платформата (DIL, NIS, COL3, VAIS)",
    ],
    pii_note_title: "Защита на личните Ви идентификатори:",
    pii_note: "Преди да изпратим каквото и да е към OpenAI, локално маскираме: ЕГН, имейл, телефон, IBAN, дата на раждане, пациентски номер. Лабораторни PDF-и се обработват локално с pdfplumber → стрипваме идентификаторите → само клиничният текст отива до AI. Никога не изпращаме раcсърви имена или адрес.",
    legal_basis_title: "Правно основание:",
    legal_basis: "чл. 6(1)(a) и чл. 9(2)(a) GDPR — изричен consent (revocable anytime). чл. 7(3) — оттегляне с един клик от Profile → Privacy. Сесия зап. в audit log (timestamp + IP) за accountability чл. 5(2).",
    rights_title:    "Вашите права:",
    rights: [
      "Достъп — експорт на цялата HI history като JSON",
      "Изтриване — пълно изтриване на всички HI snapshots (Art. 17)",
      "Преносимост — JSON експорт (Art. 20)",
      "Оттегляне — този consent може да бъде оттеглен по всяко време (Art. 7.3)",
    ],
    consent_check: "Прочетох и разбирам какво ще се обработва. Давам изрично съгласие за избраните по-горе цели.",
    btn_accept:    "Давам съгласие",
    btn_cancel:    "Назад към dashboard",
    full_policy:   "Пълна Политика за поверителност",
    err_required:  "Поне 'Персонализация' трябва да бъде избрана.",
    err_submit:    "Грешка при запис на съгласието",
  },
  en: {
    title:        "Health Intelligence — Explicit GDPR Consent",
    intro:        "Health Intelligence processes your special-category personal data (Art. 9 GDPR: health data). Before we use it, we need your explicit, informed, and voluntary consent.",
    sub_title:    "What we will process (choose which purposes you approve):",
    purposes: [
      {
        id: "personalization",
        title: "Personalisation",
        desc: "We store your conditions, symptoms and family history locally to show personalised content. No AI processing.",
        required: true,
      },
      {
        id: "ai_analysis",
        title: "AI analysis (OpenAI GPT-4o)",
        desc: "We send ANONYMISED conclusions (conditions, biomarkers, phenotype) to OpenAI Inc. (US) for personalised AI recommendations. Personal identifiers (ID number, name, email) are masked BEFORE sending. Under SCC + zero-retention mode.",
        required: false,
      },
      {
        id: "research_aggregated",
        title: "Anonymised research",
        desc: "We use FULLY anonymised (no user_id, no timestamp) population aggregates to improve algorithms (e.g. average DIL by phenotype). We cannot trace back to you.",
        required: false,
      },
    ],
    data_categories_title: "Data we will process:",
    data_categories: [
      "Chronic conditions (PCOS, Hashimoto, T2D, etc.)",
      "Symptoms (fatigue, pain, sleep issues — 0-10 scale)",
      "Family history (relation + condition only, NO names)",
      "Lab biomarkers (TSH, glucose, insulin, lipid panel, etc.)",
      "Metabolic indices computed by the platform (DIL, NIS, COL3, VAIS)",
    ],
    pii_note_title: "Personal identifier protection:",
    pii_note: "Before any data leaves to OpenAI we locally mask: national ID, email, phone, IBAN, date of birth, patient ID. Lab PDFs are processed locally with pdfplumber → identifiers stripped → only clinical text reaches the AI. We never send full names or addresses.",
    legal_basis_title: "Legal basis:",
    legal_basis: "Art. 6(1)(a) and 9(2)(a) GDPR — explicit consent (revocable anytime). Art. 7(3) — one-click withdrawal from Profile → Privacy. Session logged (timestamp + IP) for Art. 5(2) accountability.",
    rights_title:    "Your rights:",
    rights: [
      "Access — export full HI history as JSON",
      "Erasure — delete all HI snapshots (Art. 17)",
      "Portability — JSON export (Art. 20)",
      "Withdrawal — this consent can be revoked at any time (Art. 7.3)",
    ],
    consent_check: "I have read and understand what will be processed. I give explicit consent for the purposes selected above.",
    btn_accept:    "Give consent",
    btn_cancel:    "Back to dashboard",
    full_policy:   "Full Privacy Policy",
    err_required:  "At least 'Personalisation' must be selected.",
    err_submit:    "Failed to save consent",
  },
  es: {
    title:        "Health Intelligence — Consentimiento Explícito GDPR",
    intro:        "Health Intelligence procesa sus datos personales de categoría especial (Art. 9 GDPR: datos de salud). Antes de utilizarlos, necesitamos su consentimiento explícito, informado y voluntario.",
    sub_title:    "Qué procesaremos (elija los fines que aprueba):",
    purposes: [
      {
        id: "personalization",
        title: "Personalización",
        desc: "Almacenamos sus enfermedades, síntomas e historial familiar de forma local para mostrar contenido personalizado. Sin procesamiento de AI.",
        required: true,
      },
      {
        id: "ai_analysis",
        title: "Análisis con AI (OpenAI GPT-4o)",
        desc: "Enviamos conclusiones ANONIMIZADAS (enfermedades, biomarcadores, fenotipo) a OpenAI Inc. (EE. UU.) para recomendaciones personalizadas de AI. Los identificadores personales (número de identificación, nombre, correo electrónico) se enmascaran ANTES del envío. Bajo SCC + modo de retención cero.",
        required: false,
      },
      {
        id: "research_aggregated",
        title: "Investigación anonimizada",
        desc: "Utilizamos agregados poblacionales COMPLETAMENTE anonimizados (sin user_id, sin marca de tiempo) para mejorar los algoritmos (p. ej., DIL promedio por fenotipo). No es posible rastrearlos hasta usted.",
        required: false,
      },
    ],
    data_categories_title: "Datos que procesaremos:",
    data_categories: [
      "Enfermedades crónicas (SOP, Hashimoto, DM2, etc.)",
      "Síntomas (fatiga, dolor, problemas de sueño — escala 0-10)",
      "Historial familiar (relación + enfermedad únicamente, SIN nombres)",
      "Biomarcadores de laboratorio (TSH, glucosa, insulina, perfil lipídico, etc.)",
      "Índices metabólicos calculados por la plataforma (DIL, NIS, COL3, VAIS)",
    ],
    pii_note_title: "Protección de identificadores personales:",
    pii_note: "Antes de que cualquier dato sea enviado a OpenAI, enmascaramos localmente: número de identificación nacional, correo electrónico, teléfono, IBAN, fecha de nacimiento, identificador de paciente. Los PDF de laboratorio se procesan localmente con pdfplumber → los identificadores se eliminan → solo el texto clínico llega a la AI. Nunca enviamos nombres completos ni direcciones.",
    legal_basis_title: "Base legal:",
    legal_basis: "Art. 6(1)(a) y 9(2)(a) GDPR — consentimiento explícito (revocable en cualquier momento). Art. 7(3) — retirada con un solo clic desde Perfil → Privacidad. Sesión registrada (marca de tiempo + IP) para la responsabilidad del Art. 5(2).",
    rights_title:    "Sus derechos:",
    rights: [
      "Acceso — exportar el historial completo de HI en formato JSON",
      "Supresión — eliminar todas las instantáneas de HI (Art. 17)",
      "Portabilidad — exportación en JSON (Art. 20)",
      "Retirada — este consentimiento puede revocarse en cualquier momento (Art. 7.3)",
    ],
    consent_check: "He leído y entiendo qué será procesado. Doy mi consentimiento explícito para los fines seleccionados anteriormente.",
    btn_accept:    "Dar consentimiento",
    btn_cancel:    "Volver al panel",
    full_policy:   "Política de privacidad completa",
    err_required:  "Debe seleccionarse al menos 'Personalización'.",
    err_submit:    "Error al guardar el consentimiento",
  },
};

export default function HealthIntelligenceConsentModal({
  open,
  apiBase,
  onAccept,
  onCancel,
  language = "bg",
  isDark = false,
}) {
  const lang = language === "en" ? "en" : "bg";
  const t = T[lang];
  const [purposes, setPurposes] = useState({
    personalization: true,
    ai_analysis: true,
    research_aggregated: false,
  });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  if (!open) return null;

  const togglePurpose = (id, required) => {
    if (required) return;
    setPurposes((p) => ({ ...p, [id]: !p[id] }));
  };

  const accept = async () => {
    if (!agreed) return;
    if (!purposes.personalization) {
      setErrMsg(t.err_required);
      return;
    }
    setErrMsg(null);
    setSubmitting(true);
    try {
      const granted = Object.entries(purposes)
        .filter(([, v]) => v)
        .map(([k]) => k);
      await axios.post(`${apiBase}/health-intelligence/consent`, {
        granted: true,
        purposes: granted,
      });
      onAccept?.(granted);
    } catch (e) {
      setErrMsg(e.response?.data?.detail || t.err_submit);
    } finally {
      setSubmitting(false);
    }
  };

  const panelBg = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const txt = isDark ? "text-slate-100" : "text-slate-900";
  const sub = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";
  const blockBg = isDark ? "bg-slate-800/60" : "bg-slate-50";
  const cardBorder = isDark ? "border-slate-700" : "border-slate-200";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="hi-consent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hi-consent-title"
    >
      <div className={`relative w-full max-w-2xl rounded-2xl border shadow-2xl ${panelBg} max-h-[90vh] overflow-y-auto`}>
        <div className={`flex items-start gap-3 p-6 pb-4 border-b ${cardBorder}`}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 shrink-0">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="hi-consent-title" className={`text-lg sm:text-xl font-bold ${txt}`}>
              {t.title}
            </h2>
            <p className={`text-xs sm:text-sm mt-1 ${sub}`}>GDPR Art. 6(1)(a) · Art. 9(2)(a) · Art. 7(3)</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 ${muted}`}
            aria-label="Close"
            data-testid="hi-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 pt-4 space-y-4">
          <p className={`text-sm leading-relaxed ${txt}`}>{t.intro}</p>

          {/* Granular purposes */}
          <div className="space-y-2.5">
            <div className={`text-xs font-bold uppercase tracking-wide ${txt}`}>{t.sub_title}</div>
            {t.purposes.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => togglePurpose(p.id, p.required)}
                disabled={p.required}
                data-testid={`hi-consent-purpose-${p.id}`}
                className={`w-full text-left rounded-xl border p-3.5 transition flex items-start gap-3 ${cardBorder} ${blockBg} ${
                  purposes[p.id]
                    ? "ring-2 ring-violet-500/40 border-violet-500/50"
                    : "hover:border-violet-400/40"
                } ${p.required ? "opacity-90 cursor-default" : "cursor-pointer"}`}
              >
                <div
                  className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                    purposes[p.id]
                      ? "bg-violet-600 border-violet-600 text-white"
                      : isDark ? "border-slate-600" : "border-slate-300"
                  }`}
                >
                  {purposes[p.id] && <ShieldCheck className="w-3 h-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${txt} flex items-center gap-1.5`}>
                    {p.title}
                    {p.required && (
                      <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
                        {lang === "bg" ? "ЗАДЪЛЖИТЕЛНО" : "REQUIRED"}
                      </span>
                    )}
                  </div>
                  <div className={`text-[11.5px] leading-relaxed mt-1 ${sub}`}>{p.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Data categories */}
          <div className={`rounded-xl border p-4 ${blockBg} ${cardBorder}`}>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-cyan-500" />
              <span className={`text-xs font-bold uppercase tracking-wide ${txt}`}>{t.data_categories_title}</span>
            </div>
            <ul className={`space-y-1 text-[12.5px] ${sub}`}>
              {t.data_categories.map((c, i) => (
                <li key={i} className="flex gap-2"><span className="text-cyan-500 shrink-0">•</span><span>{c}</span></li>
              ))}
            </ul>
          </div>

          {/* PII protection */}
          <div className={`rounded-xl border p-4 ${blockBg} ${cardBorder}`}>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className={`text-xs font-bold uppercase tracking-wide ${txt}`}>{t.pii_note_title}</span>
            </div>
            <p className={`text-[11.5px] leading-relaxed ${sub}`}>{t.pii_note}</p>
          </div>

          {/* Legal basis */}
          <div>
            <div className={`text-xs font-bold uppercase tracking-wide ${txt} mb-1`}>{t.legal_basis_title}</div>
            <p className={`text-[11.5px] leading-relaxed ${muted}`}>{t.legal_basis}</p>
          </div>

          {/* Rights */}
          <div>
            <div className={`text-xs font-bold uppercase tracking-wide ${txt} mb-1.5 flex items-center gap-1.5`}>
              <Activity className="w-3.5 h-3.5 text-amber-500" />
              {t.rights_title}
            </div>
            <ul className={`space-y-0.5 text-[11.5px] ${sub}`}>
              {t.rights.map((r, i) => (
                <li key={i} className="flex gap-2"><span className="text-emerald-500 shrink-0">✓</span><span>{r}</span></li>
              ))}
            </ul>
          </div>

          <a
            href="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-xs underline hover:text-violet-500 ${muted}`}
            data-testid="hi-consent-privacy-link"
          >
            <Sparkles className="w-3 h-3" />
            {t.full_policy} →
          </a>

          {errMsg && (
            <div className={`text-xs p-2.5 rounded-lg flex items-start gap-2 ${isDark ? "bg-rose-900/30 text-rose-300 border border-rose-800" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid="hi-consent-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{errMsg}</span>
            </div>
          )}
        </div>

        <div className={`p-6 pt-0 border-t mt-2 pt-4 ${cardBorder}`}>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-violet-600"
              data-testid="hi-consent-checkbox"
            />
            <span className={`text-sm ${txt}`}>{t.consent_check}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              data-testid="hi-consent-cancel"
            >
              {t.btn_cancel}
            </button>
            <button
              type="button"
              onClick={accept}
              disabled={!agreed || submitting}
              className={`px-5 py-2 rounded-lg text-sm font-bold text-white transition flex items-center justify-center gap-2 ${
                agreed && !submitting
                  ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90"
                  : "bg-slate-400 cursor-not-allowed opacity-60"
              }`}
              data-testid="hi-consent-accept"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.btn_accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
